import React from 'react';
import { Box, Typography } from '@mui/material';
import Barcode from 'react-barcode';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';

const FORMAT_MAP = {
  'Code 128': 'CODE128',
};

const BarcodeLabel = ({
  itemNumber,
  barcodeValue,
  format = 'Code 128',
  itemTitle,
  listPrice,
  channelName,
  location,
  customText,
  showBarcode = true,
  showTitle = true,
  showPrice = true,
  showChannelName = false,
  showLocation = true,
  showCustomText = false,
  renderer = 'svg',
  boldAllFields = false,
}) => {
  const boldWeight = boldAllFields ? 700 : undefined;
  const resolvedBarcodeValue = barcodeValue || itemNumber;
  const isQR = format === 'QR Code';
  const barcodeFormat = FORMAT_MAP[format] || 'CODE128';
  const QRComponent = renderer === 'svg' ? QRCodeSVG : QRCodeCanvas;

  const hasText = (showTitle && itemTitle) || (showPrice && listPrice != null) ||
    (showChannelName && channelName) || (showLocation && location) || (showCustomText && customText);

  // Text-only label (no barcode)
  if (!showBarcode) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          display: 'inline-block',
          minWidth: 180,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {itemNumber}
        </Typography>
        {showTitle && itemTitle && (
          <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight || 600, lineHeight: 1.2 }} noWrap>
            {itemTitle}
          </Typography>
        )}
        {showPrice && listPrice != null && (
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            ${parseFloat(listPrice).toFixed(2)}
          </Typography>
        )}
        {showLocation && location && (
          <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight }}>
            {location}
          </Typography>
        )}
        {showChannelName && channelName && (
          <Typography variant="caption" display="block" noWrap sx={{ fontWeight: boldWeight }}>
            {channelName}
          </Typography>
        )}
        {showCustomText && customText && (
          <Typography variant="caption" display="block" noWrap sx={{ fontWeight: boldWeight }}>
            {customText}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: 'center',
        display: 'inline-block',
        '@media print': {
          '& svg': { maxWidth: '100%', height: 'auto' },
        },
      }}
    >
      {isQR ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <QRComponent value={resolvedBarcodeValue} size={80} level="M" style={{ flexShrink: 0 }} />
          {hasText && (
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              {showTitle && itemTitle && (
                <Typography variant="body2" sx={{ fontWeight: boldWeight || 600 }} noWrap>
                  {itemTitle}
                </Typography>
              )}
              {showPrice && listPrice != null && (
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  ${parseFloat(listPrice).toFixed(2)}
                </Typography>
              )}
              {showLocation && location && (
                <Typography variant="caption" display="block" noWrap sx={{ fontWeight: boldWeight }}>
                  {location}
                </Typography>
              )}
              {showChannelName && channelName && (
                <Typography variant="caption" noWrap sx={{ fontWeight: boldWeight }}>
                  {channelName}
                </Typography>
              )}
              {showCustomText && customText && (
                <Typography variant="caption" display="block" noWrap sx={{ fontWeight: boldWeight }}>
                  {customText}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Barcode
            value={resolvedBarcodeValue}
            format={barcodeFormat}
            renderer={renderer}
            displayValue={true}
            width={1.5}
            height={50}
            fontSize={12}
            margin={0}
          />

          {showTitle && itemTitle && (
            <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight || 600, lineHeight: 1.1 }} noWrap>
              {itemTitle}
            </Typography>
          )}

          {showPrice && listPrice != null && (
            <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight, lineHeight: 1.1 }}>
              ${parseFloat(listPrice).toFixed(2)}
            </Typography>
          )}

          {showLocation && location && (
            <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight, lineHeight: 1.1 }}>
              {location}
            </Typography>
          )}

          {showChannelName && channelName && (
            <Typography variant="caption" sx={{ fontWeight: boldWeight }}>
              {channelName}
            </Typography>
          )}

          {showCustomText && customText && (
            <Typography variant="caption" display="block" sx={{ fontWeight: boldWeight }}>
              {customText}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default BarcodeLabel;
